/**
 * Supabase access.
 *
 * Every table is reached through ONE generic read/write path driven by the
 * descriptor table below, rather than eleven hand-written mappings. That is
 * deliberate: a hand-written mapping per table is eleven chances to get a
 * column name wrong, and a wrong column name does not fail loudly — it produces
 * rows that look valid and are silently empty.
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import type { Store, Unit } from '../../domain/types.js';
import type { ItemMeta, SyncItem } from './merge.js';
import { PLAN_STORE_KEY } from './keys.js';
import type {
  CookValue, DinnerValue, FlagValue, LibStapleValue, LunchValue, ManualValue, RecipeValue,
  StapleValue, StoreValue,
} from './planItems.js';

type Row = Record<string, unknown>;

export interface SupabaseConfig { url: string; anonKey: string }

/**
 * Missing config is not an error. The app is fully usable without sharing —
 * that is the whole point of it working in a shop — so an absent URL simply
 * means the sync layer never runs.
 */
export function readConfig(): SupabaseConfig | null {
  const url = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
  const anonKey = import.meta.env['VITE_SUPABASE_ANON_KEY'] as string | undefined;
  if (!url || !anonKey) return null;
  return { url, anonKey };
}

let client: SupabaseClient | null = null;

export function supabase(): SupabaseClient | null {
  if (client) return client;
  const config = readConfig();
  if (!config) return null;
  client = createClient(config.url, config.anonKey, {
    auth: {
      // Long-lived, so the family password is typed once per device.
      persistSession: true,
      autoRefreshToken: true,
    },
  });
  return client;
}

export function isConfigured(): boolean {
  return readConfig() !== null;
}

export async function currentSession(): Promise<Session | null> {
  const sb = supabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  return data.session;
}

export async function signIn(email: string, password: string): Promise<{ error: string | null }> {
  const sb = supabase();
  if (!sb) return { error: 'Sharing is not configured in this build.' };
  const { error } = await sb.auth.signInWithPassword({ email, password });
  return { error: error ? error.message : null };
}

export async function signOut(): Promise<void> {
  await supabase()?.auth.signOut();
}

/** The household this session belongs to, or null when signed out. */
export async function householdId(): Promise<string | null> {
  const sb = supabase();
  if (!sb) return null;
  const { data, error } = await sb.from('household_members').select('household_id').limit(1);
  if (error || !data || data.length === 0) return null;
  return (data[0] as { household_id: string }).household_id;
}

// --------------------------------------------------------------- mapping --

export interface Mapper {
  table: string;
  /** Week-scoped tables carry week_start; library tables do not. */
  weekly: boolean;
  /** Whether the table has a deleted_at column. */
  soft: boolean;
  /** Identity columns derived from the part of the item key after its prefix. */
  idCols: (rest: string) => Row;
  /** Rebuild the item key from a row. */
  keyOf: (row: Row) => string;
  /** Payload columns. Called only for a live item. */
  payload: (value: unknown) => Row;
  /** Rebuild the payload from a row. */
  valueOf: (row: Row) => unknown;
}

const s = (v: unknown): string => String(v ?? '');
const n = (v: unknown): number => Number(v ?? 0);

export const MAPPERS: Record<string, Mapper> = {
  plan: {
    table: 'plans', weekly: true, soft: false,
    idCols: () => ({}),
    keyOf: () => PLAN_STORE_KEY,
    payload: (v) => ({ store: (v as StoreValue).store }),
    valueOf: (r) => ({ store: s(r['store']) as Store } satisfies StoreValue),
  },
  dinner: {
    table: 'plan_dinners', weekly: true, soft: true,
    idCols: (rest) => ({ slot_date: rest }),
    keyOf: (r) => `dinner:${s(r['slot_date'])}`,
    payload: (v) => {
      const d = v as DinnerValue;
      return { meal_id: d.mealId, spans_days: d.spansDays, portions: d.portions };
    },
    valueOf: (r) => ({
      mealId: s(r['meal_id']),
      spansDays: n(r['spans_days']),
      portions: r['portions'] === null || r['portions'] === undefined ? null : n(r['portions']),
    } satisfies DinnerValue),
  },
  lunch: {
    table: 'plan_lunches', weekly: true, soft: true,
    idCols: (rest) => {
      const at = rest.indexOf(':');
      return { slot_date: rest.slice(0, at), person_id: rest.slice(at + 1) };
    },
    keyOf: (r) => `lunch:${s(r['slot_date'])}:${s(r['person_id'])}`,
    payload: (v) => ({ component_ids: (v as LunchValue).componentIds }),
    valueOf: (r) => ({ componentIds: (r['component_ids'] as string[] | null) ?? [] } satisfies LunchValue),
  },
  staple: {
    table: 'plan_staples', weekly: true, soft: true,
    idCols: (rest) => ({ ingredient_id: rest }),
    keyOf: (r) => `staple:${s(r['ingredient_id'])}`,
    payload: (v) => ({ qty: (v as StapleValue).qty, unit: (v as StapleValue).unit }),
    valueOf: (r) => ({ qty: n(r['qty']), unit: s(r['unit']) as Unit } satisfies StapleValue),
  },
  manual: {
    table: 'plan_manual', weekly: true, soft: true,
    idCols: (rest) => ({ item_id: rest }),
    keyOf: (r) => `manual:${s(r['item_id'])}`,
    payload: (v) => ({ label: (v as ManualValue).label, aisle: (v as ManualValue).aisle }),
    valueOf: (r) => ({
      label: s(r['label']), aisle: s(r['aisle']) as ManualValue['aisle'],
    } satisfies ManualValue),
  },
  tick: {
    table: 'plan_ticks', weekly: true, soft: false,
    idCols: (rest) => ({ item_key: rest }),
    keyOf: (r) => `tick:${s(r['item_key'])}`,
    payload: (v) => ({ ticked: (v as FlagValue).on }),
    valueOf: (r) => ({ on: r['ticked'] === true } satisfies FlagValue),
  },
  pantry: {
    table: 'plan_pantry', weekly: true, soft: false,
    idCols: (rest) => ({ ingredient_id: rest }),
    keyOf: (r) => `pantry:${s(r['ingredient_id'])}`,
    payload: (v) => ({ have: (v as FlagValue).on }),
    valueOf: (r) => ({ on: r['have'] === true } satisfies FlagValue),
  },
  fav: {
    table: 'library_favourites', weekly: false, soft: true,
    idCols: (rest) => ({ meal_id: rest }),
    keyOf: (r) => `fav:${s(r['meal_id'])}`,
    payload: () => ({}),
    valueOf: () => ({ on: true } satisfies FlagValue),
  },
  cook: {
    table: 'library_cook_log', weekly: false, soft: true,
    idCols: (rest) => {
      const at = rest.lastIndexOf(':');
      return { meal_id: rest.slice(0, at), cooked_on: rest.slice(at + 1) };
    },
    keyOf: (r) => `cook:${s(r['meal_id'])}:${s(r['cooked_on'])}`,
    // null rating is written as null and means ASKED AND DISMISSED. It must
    // round-trip as null, never be coalesced to a value.
    payload: (v) => ({ rating: (v as CookValue).rating }),
    valueOf: (r) => ({
      rating: (r['rating'] as CookValue['rating'] | null) ?? null,
    } satisfies CookValue),
  },
  libstaple: {
    table: 'library_staples', weekly: false, soft: true,
    idCols: (rest) => ({ ingredient_id: rest }),
    keyOf: (r) => `libstaple:${s(r['ingredient_id'])}`,
    payload: (v) => {
      const l = v as LibStapleValue;
      return { qty: l.qty, unit: l.unit, usually_on: l.usuallyOn };
    },
    valueOf: (r) => ({
      qty: n(r['qty']), unit: s(r['unit']) as Unit, usually_on: r['usually_on'] === true,
    } as unknown as LibStapleValue),
  },
  recipe: {
    table: 'library_custom_meals', weekly: false, soft: true,
    idCols: (rest) => ({ meal_id: rest }),
    keyOf: (r) => `recipe:${s(r['meal_id'])}`,
    // The whole recipe is one jsonb body: one item, one row, last-write-wins.
    payload: (v) => ({ body: v as RecipeValue }),
    valueOf: (r) => r['body'] as RecipeValue,
  },
};

function kindOfKey(key: string): string {
  return key.slice(0, key.indexOf(':'));
}

function metaOf(row: Row): ItemMeta {
  const deleted = row['deleted_at'] != null;
  const meta: ItemMeta = {
    updatedAt: new Date(s(row['updated_at'])).toISOString(),
    updatedBy: s(row['updated_by']),
  };
  return deleted ? { ...meta, deleted: true } : meta;
}

/** Fetch every item the account holds for one week, plus the library. */
export async function fetchRemote(
  household: string,
  weekStart: string,
): Promise<{ plan: SyncItem[]; library: SyncItem[] }> {
  const sb = supabase();
  if (!sb) return { plan: [], library: [] };

  const plan: SyncItem[] = [];
  const library: SyncItem[] = [];

  await Promise.all(
    Object.entries(MAPPERS).map(async ([kind, m]) => {
      let query = sb.from(m.table).select('*').eq('household_id', household);
      if (m.weekly) query = query.eq('week_start', weekStart);
      const { data, error } = await query;
      if (error || !data) return;
      for (const raw of data as Row[]) {
        const meta = metaOf(raw);
        const item: SyncItem = {
          key: m.keyOf(raw),
          value: meta.deleted === true ? null : m.valueOf(raw),
          meta,
        };
        (m.weekly ? plan : library).push(item);
      }
      void kind;
    }),
  );

  return { plan, library };
}

/**
 * Upsert items. Grouped per table so each kind is one round trip rather than
 * one per item — a full week is a few dozen rows and a phone in a shop may have
 * a very short window of signal.
 */
export async function pushItems(
  household: string,
  weekStart: string,
  items: SyncItem[],
): Promise<{ error: string | null }> {
  const sb = supabase();
  if (!sb) return { error: 'Sharing is not configured in this build.' };

  const byTable = new Map<string, { mapper: Mapper; rows: Row[] }>();

  for (const item of items) {
    const kind = kindOfKey(item.key);
    const mapper = MAPPERS[kind];
    // A key this build does not understand is left alone rather than dropped.
    // It belongs to a newer version and is not ours to delete.
    if (!mapper) continue;

    const rest = item.key.slice(item.key.indexOf(':') + 1);
    const gone = item.value === null || item.meta.deleted === true;

    // A table with no deleted_at cannot express a tombstone, so there is
    // nothing to send. Its "deletion" is already carried as a value.
    if (gone && !mapper.soft) continue;

    const row: Row = {
      household_id: household,
      ...(mapper.weekly ? { week_start: weekStart } : {}),
      ...mapper.idCols(rest),
      updated_at: item.meta.updatedAt,
      updated_by: item.meta.updatedBy,
      ...(mapper.soft ? { deleted_at: gone ? item.meta.updatedAt : null } : {}),
      ...(gone ? {} : mapper.payload(item.value)),
    };

    // A tombstone still has to satisfy NOT NULL columns, so the payload is
    // filled with whatever the row already had - or, on a first write, with
    // harmless defaults. Postgres rejects the whole batch otherwise.
    if (gone) Object.assign(row, tombstoneFiller(kind));

    const bucket = byTable.get(mapper.table) ?? { mapper, rows: [] };
    bucket.rows.push(row);
    byTable.set(mapper.table, bucket);
  }

  for (const [table, { rows }] of byTable) {
    const { error } = await sb.from(table).upsert(rows);
    if (error) return { error: `${table}: ${error.message}` };
  }
  return { error: null };
}

/**
 * NOT NULL columns still have to hold something on a tombstone row. These
 * values are never read — `fetchRemote` returns `value: null` whenever
 * `deleted_at` is set — they exist only to satisfy the constraint.
 */
function tombstoneFiller(kind: string): Row {
  switch (kind) {
    case 'dinner': return { meal_id: '', spans_days: 1, portions: null };
    case 'lunch': return { component_ids: [] };
    case 'staple': return { qty: 0, unit: 'g' };
    case 'manual': return { label: '', aisle: 'household' };
    case 'libstaple': return { qty: 0, unit: 'g', usually_on: false };
    case 'recipe': return { body: {} };
    default: return {};
  }
}

export async function fetchSettingsRow(household: string): Promise<Row | null> {
  const sb = supabase();
  if (!sb) return null;
  const { data, error } = await sb.from('settings').select('*').eq('household_id', household).limit(1);
  if (error || !data || data.length === 0) return null;
  return data[0] as Row;
}

export async function pushSettingsRow(household: string, row: Row): Promise<{ error: string | null }> {
  const sb = supabase();
  if (!sb) return { error: 'Sharing is not configured in this build.' };
  const { error } = await sb.from('settings').upsert({ household_id: household, ...row });
  return { error: error ? error.message : null };
}

/**
 * Server time, used to clamp a device whose clock is wrong. Falls back to the
 * local clock: a missing server time must not stop a sync, it only means the
 * clamp is skipped for that round.
 */
export async function serverNowMs(): Promise<number> {
  const sb = supabase();
  if (!sb) return Date.now();
  const { data, error } = await sb.rpc('now_ms');
  if (error || typeof data !== 'number') return Date.now();
  return data;
}

/** Test seam: drop the memoised client so a test can swap the config. */
export function resetClientForTests(): void {
  client = null;
}

/** The signed-in user's access token, for the paste-a-recipe function. Null when signed out. */
export async function accessToken(): Promise<string | null> {
  const session = await currentSession();
  return session?.access_token ?? null;
}
